import { FC } from 'react'
import Swal from 'sweetalert2'
import { IconPaths, showAlertLoading, showConfirmAlert, showError } from '_metronic/helpers'
import { RowActions, useModal, useToast } from 'app/components'
import { MedicamentoModal } from '../../components/modal/MedicamentoModal'
import { alterarSituacaoMedicamento, getMedicamentoById } from '../../core/_requests'
import { MedicamentoList } from '../../core/_models'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  medicamento: MedicamentoList
}

/**
 * Editar (dados) e inativar/reativar (situação, PATCH). Não há "excluir": receita antiga aponta para o
 * medicamento, e o que sai de uso é inativado — some da busca da prescrição e fica no histórico.
 */
export const MedicamentosActionsCell: FC<Props> = ({ medicamento }) => {
  const { showModal } = useModal()
  const { showToast } = useToast()
  const { refetch } = useQueryResponse()
  const { t } = useT()
  const nome = medicamento.nome || t('medicamento.label')
  const ativo = medicamento.ativo !== false

  const openEditModal = () =>
    showModal({
      title: t('modal.editar_medicamento'),
      content: <MedicamentoModal />,
      fetchers: {
        medicamento: () => getMedicamentoById(medicamento.id),
      },
    })

  const alternarSituacao = async () => {
    const confirmed = await showConfirmAlert({
      title: ativo ? 'Inativar medicamento?' : 'Reativar medicamento?',
      text: ativo
        ? `${nome} deixa de aparecer na busca da prescrição. Receitas já emitidas não mudam.`
        : `${nome} volta a aparecer na busca da prescrição.`,
      confirmButtonText: ativo ? 'Inativar' : 'Reativar',
      cancelButtonText: 'Cancelar',
      danger: ativo,
    })
    if (!confirmed) return

    try {
      showAlertLoading()
      await alterarSituacaoMedicamento(medicamento.id, !ativo)
      Swal.close()
      showToast({
        variant: 'success',
        title: ativo ? `${nome} foi inativado.` : `${nome} foi reativado.`,
      })
      refetch()
    } catch (error) {
      showError(error)
    }
  }

  return (
    <RowActions
      itemName={nome}
      primaryAction={{ label: 'Editar', onClick: openEditModal, icon: IconPaths.Pen }}
      items={[
        ativo
          ? { label: 'Inativar', onClick: alternarSituacao, danger: true }
          : { label: 'Reativar', onClick: alternarSituacao },
      ]}
    />
  )
}
